import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "pw-sidebar-collapsed";

type SidebarCollapsedContextValue = {
  collapsed: boolean;
  toggle: () => void;
  setCollapsed: (next: boolean) => void;
};

export const SidebarCollapsedContext = createContext<SidebarCollapsedContextValue | null>(null);

function readInitial(key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

/**
 * App-wide sidebar collapsed state (rule S6, cross-tree client state). Persists to
 * localStorage so the preference survives refreshes. Activates ⌘\ / Ctrl+\ globally.
 */
export function SidebarCollapsedProvider({
  children,
  scope = "workspace",
}: {
  children: ReactNode;
  scope?: "workspace" | "payroll";
}) {
  const [preferences, setPreferences] = useState(() => ({
    workspace: readInitial(STORAGE_KEY),
    payroll: readInitial("pw-payroll-sidebar-collapsed"),
  }));
  const collapsed = preferences[scope];
  const storageKey = scope === "payroll" ? "pw-payroll-sidebar-collapsed" : STORAGE_KEY;

  const setCollapsed = useCallback(
    (next: boolean) => {
      setPreferences((previous) => ({ ...previous, [scope]: next }));
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        /* storage unavailable */
      }
    },
    [scope, storageKey],
  );

  const toggle = useCallback(() => setCollapsed(!collapsed), [collapsed, setCollapsed]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        toggle();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  const value = useMemo(
    () => ({ collapsed, toggle, setCollapsed }),
    [collapsed, toggle, setCollapsed],
  );

  return (
    <SidebarCollapsedContext.Provider value={value}>{children}</SidebarCollapsedContext.Provider>
  );
}

export function useSidebarCollapsed() {
  const ctx = useContext(SidebarCollapsedContext);
  if (!ctx) throw new Error("useSidebarCollapsed must be used within <SidebarCollapsedProvider>");
  return ctx;
}
